import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { SLASummary } from '../../../@types/service.type';
import { COLORS } from '../../../configs/colors';

const flash = keyframes`0%,100%{opacity:1}50%{opacity:0.3}`;

interface SlaUrgency {
  color: string;
  label: string;
  flash: boolean;
}

const formatRemaining = (hours: number): string => {
  if (hours < 0) return 'Breached';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
  const days = Math.floor(hours / 24);
  const h = Math.floor(hours % 24);
  return `${days}d ${h}h`;
};

const getSlaUrgency = (hoursRemaining: number, breached: boolean): SlaUrgency => {
  if (breached) return { color: COLORS.SLAUrgency0, label: 'Breached', flash: false };
  if (hoursRemaining < 0) return { color: COLORS.SLAUrgency1, label: 'Breached', flash: true };
  if (hoursRemaining < 4) return { color: COLORS.SLAUrgency1, label: `${formatRemaining(hoursRemaining)}`, flash: true };
  if (hoursRemaining < 8) return { color: COLORS.SLAUrgency2, label: `${formatRemaining(hoursRemaining)}`, flash: false };
  if (hoursRemaining < 16) return { color: COLORS.SLAUrgency3, label: `${formatRemaining(hoursRemaining)}`, flash: false };
  if (hoursRemaining < 24) return { color: COLORS.SLAUrgency4, label: `${formatRemaining(hoursRemaining)}`, flash: false };
  if (hoursRemaining < 48) return { color: COLORS.SLAUrgency5, label: `${formatRemaining(hoursRemaining)}`, flash: false };
  return { color: COLORS.SLAUrgency6, label: `${formatRemaining(hoursRemaining)}`, flash: false };
};

export interface SlaBurndownProps {
  label: string;
  sla?: SLASummary | string | null;
}

/** SLA burndown bar — visual at-a-glance indicator with CMS1 colour rules. */
export const SlaBurndown: React.FC<SlaBurndownProps> = ({ label, sla }) => {
  if (!sla || typeof sla === 'string') return null;
  if (!sla.timeoutAt) return null;

  const timeoutMs = new Date(sla.timeoutAt).getTime();
  const nowMs = Date.now();
  const remainingMs = timeoutMs - nowMs;
  const hoursRemaining = remainingMs / (1000 * 60 * 60);
  const breached = sla.slaOutcome?.outcomeTypeCode === 'breached';

  // Progress: 0% = full time left, 100% = breached. Assume 48h total window.
  const totalWindow = 48;
  const elapsed = totalWindow - Math.max(0, hoursRemaining);
  const progress = Math.min(100, Math.max(0, (elapsed / totalWindow) * 100));

  const urgency = getSlaUrgency(hoursRemaining, breached);

  return (
    <Tooltip
      title={`${label}: ${urgency.label} remaining — expires ${new Date(sla.timeoutAt).toLocaleString()}`}
      placement="bottom"
      arrow
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 120, maxWidth: 240, flex: '0 1 auto' }}>
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
          <Box
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${label} SLA progress`}
            sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'v4.filterInput', overflow: 'hidden' }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progress}%`,
                bgcolor: urgency.color,
                borderRadius: 3,
                transition: 'width 0.5s ease',
                ...(urgency.flash ? { animation: `${flash} 1.5s ease-in-out infinite` } : {}),
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 600,
              color: urgency.color,
              whiteSpace: 'nowrap',
              ...(urgency.flash ? { animation: `${flash} 1.5s ease-in-out infinite` } : {}),
            }}
          >
            {urgency.label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};
